/**
 * The single app-level choke point for declarative messages (TEC-465 P2).
 *
 * Use `<Msg id=… defaultMessage=… />` in place of bare `<FormattedMessage>`. A
 * codemod migrates existing call sites (see scripts/translate-i18n/codemod-msg.mjs).
 *
 * OFF (production / normal users): returns `<FormattedMessage {...props} />`
 * verbatim: no wrapper element, no markers, no recording. One context read +
 * one falsy branch, nothing else.
 *
 * ON (reviewer + toggled): wraps output in a marker carrying `data-i18n-id` for
 * the overlay to find, and records `{id, route}` into the usage index. While the
 * reviewer has "Show English" on, the marker renders the English catalogue instead.
 */
import React, { useCallback, useEffect } from 'react';
import { FormattedMessage, IntlProvider, useIntl, type IntlShape, type MessageDescriptor } from 'react-intl';
import { useI18nInspector } from './inspector';
import { recordUsage } from './usage-recorder';

type FormattedMessageProps = React.ComponentProps<typeof FormattedMessage>;

const MARKER_ATTR = 'data-i18n-id';

function currentRoute(): string {
  return typeof window !== 'undefined' ? window.location.pathname : '';
}

const ignoreMissing = (): void => {};

function InspectedMessage(props: FormattedMessageProps): React.ReactElement {
  const id = String(props.id ?? '');
  const { showEnglish, englishMessages } = useI18nInspector();
  useEffect(() => {
    if (id) recordUsage(id, currentRoute());
  }, [id]);
  // The overlay's popover shows the id and English original, so no native title tooltip here.
  const englishCatalogue = showEnglish && englishMessages && id in englishMessages ? englishMessages : null;
  return (
    <span
      {...{ [MARKER_ATTR]: id }}
      className="i18n-inspect-marker"
      data-i18n-inspectable=""
      data-i18n-showing-english={englishCatalogue ? '' : undefined}
    >
      {englishCatalogue ? (
        // Same values (rich-text callbacks included) rendered against the English catalogue.
        <IntlProvider locale="en" messages={englishCatalogue} onError={ignoreMissing}>
          <FormattedMessage {...props} />
        </IntlProvider>
      ) : (
        <FormattedMessage {...props} />
      )}
    </span>
  );
}

export function Msg(props: FormattedMessageProps): React.ReactElement {
  const { enabled } = useI18nInspector();
  if (!enabled) return <FormattedMessage {...props} />;
  return <InspectedMessage {...props} />;
}

/**
 * Imperative-path equivalent (aria-label, title, placeholder, document title).
 * These produce plain strings and cannot carry a DOM marker, so they are not
 * in-place editable, but we still record `{id, route}` so the catalogue link
 * resolves to a route. Editing happens in the catalogue.
 */
export function useFormatMessage(): IntlShape['formatMessage'] {
  const intl = useIntl();
  const { enabled } = useI18nInspector();
  return useCallback(
    ((descriptor: MessageDescriptor, values?: Record<string, unknown>, opts?: unknown) => {
      if (enabled && descriptor?.id) {
        recordUsage(String(descriptor.id), currentRoute());
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return (intl.formatMessage as any)(descriptor, values, opts);
    }) as IntlShape['formatMessage'],
    [intl, enabled],
  );
}
